/**
 * パフォーマンス監視ユーティリティ
 * 非機能要件のパフォーマンス要件を満たすための監視・計測機能
 */

import logger from './logger';

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

interface PerformanceThresholds {
  apiResponse: number; // 5秒以内
  dispatchCalculation: number; // 30秒以内
  pageTransition: number; // 3秒以内
  realtimeNotification: number; // 1秒以内
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private static instance: PerformanceMonitor;

  private readonly thresholds: PerformanceThresholds = {
    apiResponse: 5000, // 5秒
    dispatchCalculation: 30000, // 30秒
    pageTransition: 3000, // 3秒
    realtimeNotification: 1000, // 1秒
  };

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * パフォーマンス計測開始
   */
  startMeasure(name: string, metadata?: Record<string, any>): void {
    const metric: PerformanceMetric = {
      name,
      startTime: performance.now(),
      metadata,
    };

    this.metrics.set(name, metric);

    if (process.env.NODE_ENV === 'development') {
      logger.info(`🚀 Performance: Started measuring "${name}"`);
    }
  }

  /**
   * パフォーマンス計測終了
   */
  endMeasure(name: string): PerformanceMetric | null {
    const metric = this.metrics.get(name);

    if (!metric) {
      logger.warn(`⚠️ Performance: No measurement found for "${name}"`);
      return null;
    }

    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;

    // 閾値チェック
    this.checkThreshold(metric);

    // ログ出力
    this.logMetric(metric);

    // メトリクス削除
    this.metrics.delete(name);

    return metric;
  }

  /**
   * API呼び出しのパフォーマンス監視ラッパー
   */
  async monitorApiCall<T>(
    name: string,
    apiCall: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.startMeasure(name, { type: 'api', ...metadata });

    try {
      const result = await apiCall();
      this.endMeasure(name);
      return result;
    } catch (error) {
      const metric = this.metrics.get(name);
      if (metric) {
        metric.endTime = performance.now();
        metric.duration = metric.endTime - metric.startTime;
        this.logMetric(metric, 'error');
        this.metrics.delete(name);
      }
      throw error;
    }
  }

  /**
   * React コンポーネントのレンダリング時間監視
   */
  monitorComponentRender(componentName: string): {
    start: () => void;
    end: () => void;
  } {
    const measureName = `component-render-${componentName}`;

    return {
      start: () =>
        this.startMeasure(measureName, {
          type: 'component-render',
          component: componentName,
        }),
      end: () => this.endMeasure(measureName),
    };
  }

  /**
   * 配車計算専用の監視メソッド
   */
  async monitorDispatchCalculation<T>(
    calculationFn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    return this.monitorApiCall('dispatch-calculation', calculationFn, {
      type: 'dispatch',
      ...metadata,
    });
  }

  /**
   * ページ遷移時間の監視
   */
  monitorPageTransition(
    fromPage: string,
    toPage: string
  ): {
    end: () => void;
  } {
    const measureName = `page-transition-${fromPage}-to-${toPage}`;
    this.startMeasure(measureName, {
      type: 'page-transition',
      from: fromPage,
      to: toPage,
    });

    return {
      end: () => this.endMeasure(measureName),
    };
  }

  /**
   * 閾値チェック
   */
  private checkThreshold(metric: PerformanceMetric): void {
    if (!metric.duration) return;

    const thresholdKey = this.getThresholdKey(metric);
    if (!thresholdKey) return;

    const threshold = this.thresholds[thresholdKey];

    if (metric.duration > threshold) {
      logger.warn(
        `⚠️ Performance Warning: "${metric.name}" took ${metric.duration.toFixed(2)}ms ` +
          `(threshold: ${threshold}ms)`
      );

      // 本番環境では外部監視システムに送信
      if (process.env.NODE_ENV === 'production') {
        this.sendToMonitoringSystem(metric, threshold);
      }
    }
  }

  /**
   * 閾値キーの取得
   */
  private getThresholdKey(metric: PerformanceMetric): keyof PerformanceThresholds | null {
    if (!metric.metadata?.type) return null;

    switch (metric.metadata.type) {
      case 'api':
        return 'apiResponse';
      case 'dispatch':
        return 'dispatchCalculation';
      case 'page-transition':
        return 'pageTransition';
      case 'notification':
        return 'realtimeNotification';
      default:
        return null;
    }
  }

  /**
   * メトリクスログ出力
   */
  private logMetric(metric: PerformanceMetric, status: 'success' | 'error' = 'success'): void {
    const duration = metric.duration?.toFixed(2) || 'N/A';
    const emoji = status === 'success' ? '✅' : '❌';

    if (process.env.NODE_ENV === 'development') {
      logger.info(
        `${emoji} Performance: "${metric.name}" completed in ${duration}ms`,
        metric.metadata
      );
    }

    // 構造化ログとして出力（本番環境用）
    const logData = {
      timestamp: new Date().toISOString(),
      type: 'performance_metric',
      name: metric.name,
      duration: metric.duration,
      status,
      metadata: metric.metadata,
    };

    // 本番環境では外部ログシステムに送信
    if (process.env.NODE_ENV === 'production') {
      this.sendToLogSystem(logData);
    }
  }

  /**
   * 外部監視システムへの送信（プレースホルダー）
   */
  private sendToMonitoringSystem(metric: PerformanceMetric, threshold: number): void {
    // 将来的にOpenTelemetry等の監視システムと連携
    console.info('Sending performance alert to monitoring system', {
      metric,
      threshold,
    });
  }

  /**
   * 外部ログシステムへの送信（プレースホルダー）
   */
  private sendToLogSystem(logData: Record<string, unknown>): void {
    // 将来的にCloudWatch、Datadog等のログシステムと連携
    console.info('Sending to log system', logData);
  }

  /**
   * 現在の計測中メトリクス一覧取得
   */
  getActiveMetrics(): string[] {
    return Array.from(this.metrics.keys());
  }

  /**
   * 全メトリクスクリア
   */
  clearMetrics(): void {
    this.metrics.clear();
  }
}

// シングルトンインスタンスのエクスポート
export const performanceMonitor = PerformanceMonitor.getInstance();

// React Hook用のカスタムフック
export function usePerformanceMonitor() {
  return {
    startMeasure: performanceMonitor.startMeasure.bind(performanceMonitor),
    endMeasure: performanceMonitor.endMeasure.bind(performanceMonitor),
    monitorApiCall: performanceMonitor.monitorApiCall.bind(performanceMonitor),
    monitorComponentRender: performanceMonitor.monitorComponentRender.bind(performanceMonitor),
    monitorDispatchCalculation:
      performanceMonitor.monitorDispatchCalculation.bind(performanceMonitor),
    monitorPageTransition: performanceMonitor.monitorPageTransition.bind(performanceMonitor),
  };
}

export type { PerformanceMetric, PerformanceThresholds };
